import { Component, OnInit, OnDestroy, Input, Output, ViewChild, TemplateRef, EventEmitter, signal } from '@angular/core';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '../../services/toast.service';
import { LosTokenService } from 'src/app/services/los-token.service';
import { LOSToken } from 'src/app/models/los-token';
import { LOSTokenData } from 'src/app/models/los-token-data';
import { MatDialog } from '@angular/material/dialog';
import { Integration } from 'src/app/models/integration';
import { FormGroup } from '@angular/forms';
import { UntypedFormControl } from '@angular/forms';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { createDeleteButton } from 'src/app/features/grid/classes/grid-config.types';

@Component({
  selector: 'app-los-token-mapping-view',
  templateUrl: './los-token-mapping-view.component.html',
  styleUrls: ['./los-token-mapping-view.component.scss'],
  standalone: false,
})
export class LosTokenMappingViewComponent implements OnInit, OnDestroy {
  componentDestroyed$ = new Subject<boolean>();
  @Input() data: LOSToken;

  public addForm: FormGroup = new FormGroup({
    integration: new UntypedFormControl(),
  });

  @Output() mappingDataChanged = new EventEmitter<string>();

  @ViewChild('confirmDeleteDialog', { static: true }) confirmDialog: TemplateRef<any>;
  @ViewChild('confirmDeleteTokenDialog', { static: true }) confirmTokenDialog: TemplateRef<any>;
  @ViewChild('confirmDeleteAllDialog', { static: true }) confirmAllDialog: TemplateRef<any>;
  @ViewChild('addMappingDialog', { static: true }) addDialog: TemplateRef<any>;

  public itemToRemove: LOSTokenData = null;
  public itemToAdd: LOSToken = null;

  gettingIntegrations = false;
  haveIntegrations = false;

  allGroup: LOSTokenData[] = [];

  public gridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);
  settingsReady = false;

  allIntegrations: Integration[] = [];
  filteredIntegrations: Integration[] = [];
  availableIntegrations: Integration[] = [];
  selectedIntegration: Integration = null;

  clientId: number = null;

  validateClientId() {
    if (this.allGroup.length > 0) {
      const cIds = this.allGroup.map(x => x.clientId);
      const unique = cIds.filter((value, index, self) => self.indexOf(value) === index);
      if (unique.length == 1 && unique[0]) {
        this.clientId = unique[0];
      }
      else {
        this.toastService.toastCreate('Multiple Client Ids found for token.', 'Warning');
      }
    }
  }

  constructor(
    private toastService: ToastService,
    private lTService: LosTokenService,
    private clientService: ClientIntegrationService,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.setupGrid();
    if (this.data && this.data.losTokenId != '') {
      this.getMappings();
    }
    else {
      this.settingsReady = true;
      this.toastService.toastCreate('Passed in LOSToken or LOSTokenId is null.', 'Warning');
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getMappings(): void {
    this.allGroup = [];

    this.lTService.getLOSTokenMappingDataForTokenIdWithoutTokenValue(this.data.losTokenId)
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe(
      (_data) => {
        if (_data != null && _data.length > 0) {
          _data.forEach(x => {
            this.allGroup.push(new LOSTokenData(x));
          });

          this.validateClientId();
        }
        this.gridLoading.set(false);
        this.updateGrid();
      },
    );
  }

  reloadData(parentReload = false): void {
    this.selectedIntegration = null;
    this.itemToAdd = null;
    this.itemToRemove = null;
    if (parentReload == true) {
      this.mappingDataChanged.emit(this.data.losTokenId);
    }
    else {
      this.getMappings();
    }
  }

  //--------------------------- Dialog Functions ------------------------------------------//
  closeDialog(): void {
    this.dialog.closeAll();
  }

  //--------------------------- DELETE ------------------------------------------//

  startDeleteAll() {
    this.itemToRemove = new LOSTokenData(this.data);
    this.dialog.open(this.confirmAllDialog);
  }

  confirmDelete(x?: LOSTokenData) {
    this.itemToRemove = x;
    this.dialog.open(this.confirmDialog);
  }

  checkDeleteMapping() {
    this.dialog.closeAll();
    if (this.allGroup.length <= 1) {
      this.dialog.open(this.confirmTokenDialog);
    }
    else {
      this.deleteMapping(this.itemToRemove);
    }
  }

  private deleteMapping(mapping: LOSTokenData) {
    if (mapping != null && mapping.losTokenMappingId != -1) {
      this.lTService.deleteLOSTokenMapping(mapping.losTokenMappingId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_value) => {
          // success
          this.toastService.toastCreate('Successfully deleted mapping', 'Success', {
            autoClose: false,
          });
          this.reloadData();
        },
        error: (_error) => {
          this.toastService.toastCreate('Error deleting mapping', 'Warning', {
            autoClose: false,
          });
        },
      });
    }
  }

  deleteToken() {
    this.dialog.closeAll();

    if (this.itemToRemove != null) {
      this.lTService.deleteLOSToken(this.itemToRemove)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_value) => {
          this.toastService.toastCreate('Successfully deleted token and mapping', 'Success', {
            autoClose: false,
          });
          this.reloadData(true);
        },
        error: (_error) => {
          this.toastService.toastCreate('Error deleting token and mapping', 'Warning', {
            autoClose: false,
          });
        },
      });
    }
  }

  //--------------------------- INSERT ------------------------------------------//
  startAdd() {
    this.itemToAdd = null;
    this.selectedIntegration = null;
    this.getIntegrationsForClient();
    this.dialog.open(this.addDialog);
  }

  addNewMapping() {
    this.dialog.closeAll();
    if (this.selectedIntegration != null) {
      this.itemToAdd = new LOSToken(this.data);
      this.itemToAdd.integrationId = this.selectedIntegration.integrationId;
      this.lTService.insertLOSTokenMapping(this.itemToAdd)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_data) => {
          // success
          this.toastService.toastCreate('Successfully added mapping.', 'Success', {
            autoClose: false,
          });
          this.reloadData();
        },
        error: (_error) => {
          this.toastService.toastCreate('Error adding mapping.', 'Warning', {
            autoClose: false,
          });
        },
      });
    }
  }

  //--------------------------- Integration Functions ------------------------------------------//
  getIntegrationsForClient(): void {
    this.gettingIntegrations = true;
    if (this.clientId != null) {
      this.clientService.getIntegrationsByClientId(this.clientId, true)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_data) => {
          if (_data != null && _data.length > 0) {
            this.haveIntegrations = true;
            this.gettingIntegrations = false;

            // filter by data source but group togerher encompass and encompass api
            if (this.data.dataSourceTypeId == 'DynamicEncompass' || this.data.dataSourceTypeId == 'DynamicEncompassAPI') {
              this.allIntegrations = _data.filter(x => x.dataSourceTypeId == 'DynamicEncompass' || x.dataSourceTypeId == 'DynamicEncompassAPI');
            }
            else {
              this.allIntegrations = _data.filter(x => x.dataSourceTypeId == this.data.dataSourceTypeId);
            }

            // filter out integrations that are already linked to the token
            const activeIntegrationIds = this.allGroup.map(x => x.integrationId);
            this.availableIntegrations = this.allIntegrations.filter(
              x => activeIntegrationIds.indexOf(x.integrationId) == -1,
            );
            this.selectedIntegration = this.availableIntegrations[0];
          }
        },
        error: (_error) => {
          this.gettingIntegrations = false;
          this.toastService.toastCreate('Error getting integrations.', 'Warning', {
            autoClose: false,
          });
        },
      });
    }
  }

  displayIntegration(integration: Integration): string {
    return integration.name;  
  }

  selectIntegration(integration: Integration) {
    this.selectedIntegration = integration;
  }

  //--------------------------- Grid Functions ------------------------------------------//
  setupGrid(): void {
    this.gridSettings.set(new GridSettings({
      data: [],
      loading: this.gridLoading,
      columns: [
        new GridColumnData({ field: 'clientId', header: 'ClientId', width: '130px', minWidth: '50px'}),
        new GridColumnData({ field: 'integrationId', header: 'IntegrationId', width: '50px', minWidth: '50px'}),
        new GridColumnData({ field: 'integrationName', header: 'IntegrationName', width: '150px', minWidth: '50px'}),
      ],
      sortMode: 'single',
      dataKey: 'losTokenMappingId',
      filter: {enabled: false, mode: 'row', display: 'row'},
      selection: {
        enabled: true,
        mode: 'single',
        selectionType: 'row',
      },
      commandsColumn: {
        enabled: true,
        headerText: 'Delete',
        layout: 'horizontal',
        buttonSpacing: '0.25rem',
        buttons: [
          createDeleteButton(),
        ],
      },
    }));
    this.settingsReady = true;
  }

  updateGrid() {
    this.gridSettings.set(new GridSettings({
      ...this.gridSettings(),
      data: this.allGroup,
      loading: this.gridLoading,
    }));
  }
}
